import { useState } from 'react'
import {
  Stack, Text, Table, Button, Badge, Modal, TextInput, Textarea,
  NumberInput, Group, Alert, MultiSelect, Select, ActionIcon,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconPlus, IconAlertCircle, IconAlertTriangle, IconUsers, IconBuilding,
  IconPower,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { selectableUsers, type MixedUserRow } from '@/utils/realUsers'
import { DataCard } from '@/components/ui'
import {
  ccmApi, APPROVER_ROLE_LABELS,
  type ApproverRole, type CommitteeSummary,
} from '@/api/ccm'

function apiErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (typeof detail !== 'string') return fallback
  // organizacao_ja_coberta:10:CCM Infra → nomeia o comitê que já cobre
  if (detail.startsWith('organizacao_ja_coberta:')) {
    const [, orgId, comite] = detail.split(':')
    return `A organização #${orgId} já é coberta pelo comitê "${comite}". Remova-a de lá antes de atribuí-la aqui.`
  }
  if (detail.startsWith('nome_duplicado:')) {
    return `Já existe um comitê chamado "${detail.split(':')[1]}".`
  }
  if (detail.startsWith('quorum_invalido')) return 'O quórum mínimo é 1.'
  return detail
}

export function ComitesAdmin() {
  const queryClient = useQueryClient()
  const [novoAberto, setNovoAberto] = useState(false)
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [quorum, setQuorum] = useState<number | string>(2)
  const [editandoId, setEditandoId] = useState<number | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['ccm-committees'],
    queryFn: async () => (await ccmApi.list()).data,
  })
  const comites: CommitteeSummary[] = data?.items ?? []

  const { data: cobertura } = useQuery({
    queryKey: ['ccm-coverage'],
    queryFn: async () => (await ccmApi.coverage()).data,
  })

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['ccm-committees'] })
    queryClient.invalidateQueries({ queryKey: ['ccm-coverage'] })
  }

  const criar = useMutation({
    mutationFn: async () => ccmApi.create({
      name: nome.trim(),
      description: descricao.trim() || undefined,
      cab_quorum_required: Number(quorum) || 2,
    }),
    onSuccess: () => {
      invalidar()
      notifications.show({ color: 'green', message: 'Comitê criado.' })
      setNovoAberto(false); setNome(''); setDescricao(''); setQuorum(2)
    },
    onError: (e) => notifications.show({
      color: 'red', message: apiErrorMessage(e, 'Erro ao criar o comitê.'),
    }),
  })

  const desativar = useMutation({
    mutationFn: async (id: number) => ccmApi.deactivate(id),
    onSuccess: () => {
      invalidar()
      notifications.show({ color: 'green', message: 'Comitê desativado.' })
    },
    onError: (e) => notifications.show({
      color: 'red', message: apiErrorMessage(e, 'Erro ao desativar.'),
    }),
  })

  const orgsDescobertas = cobertura?.orgs_sem_comite ?? []
  const tenantsAmbiguos = cobertura?.tenants_ambiguos ?? []
  const isentasComComite = cobertura?.orgs_isentas_com_comite ?? []
  const tenantsSemOrg = cobertura?.tenants_sem_organizacao ?? []
  const membrosSemAlcance = cobertura?.membros_sem_alcance ?? []
  // Tenant produtivo fora de qualquer organização é o caso mais grave da lista:
  // não há organização, logo não há comitê, logo não há controle — e antes de
  // 08/08 ele não aparecia em tela nenhuma.
  const orfaosProdutivos = tenantsSemOrg.filter((t) => t.environment === 'prd')

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Não foi possível carregar os comitês. Tente novamente.
        </Alert>
      )}

      {/* O painel de cobertura vem ANTES da lista de propósito: é ele que diz
          se dá para endurecer o gate de controle de mudança. */}
      {orgsDescobertas.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow"
               title={`${orgsDescobertas.length} organização(ões) sem controle de mudança`}>
          <Text size="sm">
            Estas organizações <b>não passam por GMUD</b>: sem comitê cadastrado,
            nenhuma mudança nos tenants delas precisa de aprovação — inclusive em
            produção, e inclusive as ações que destroem infraestrutura. Cadastrar
            um comitê é o que liga o controle.
          </Text>
          <Group gap="xs" mt="xs">
            {orgsDescobertas.map((o) => (
              <Badge key={o.id} variant="light" color="yellow">
                {o.name} ({o.tenant_count} tenant{o.tenant_count === 1 ? '' : 's'})
              </Badge>
            ))}
          </Group>
        </Alert>
      )}

      {/* Tenant produtivo fora de qualquer organização: o caso mais grave, e o
          que menos aparecia — o painel enumerava organizações, nunca tenants
          soltos. Em vermelho porque não há cadastro que o cubra: ele precisa
          entrar numa organização. */}
      {orfaosProdutivos.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red"
               title={`${orfaosProdutivos.length} tenant(s) de produção fora de qualquer organização`}>
          <Text size="sm">
            Estes tenants não pertencem a organização nenhuma, então não há
            comitê que os alcance e <b>nada neles passa por GMUD</b> — mesmo
            sendo produção. Associe cada um a uma organização.
          </Text>
          <Group gap="xs" mt="xs">
            {orfaosProdutivos.map((t) => (
              <Badge key={t.tenant_id} variant="light" color="red">
                {t.name} (#{t.tenant_id})
              </Badge>
            ))}
          </Group>
        </Alert>
      )}

      {/* Isenta COM comitê: o único caso em que a isenção explícita ainda
          decide algo — há comitê cadastrado, mas a organização foi marcada como
          fora do controle de mudança. Informativo, não é pendência. */}
      {isentasComComite.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="blue"
               title={`${isentasComComite.length} organização(ões) isenta(s), apesar de terem comitê`}>
          <Text size="sm">
            Estas organizações têm comitê cadastrado, mas foram marcadas como
            isentas de controle de mudança — então não geram GMUD. É decisão
            registrada em auditoria, não falta de cadastro.
          </Text>
          <Group gap="xs" mt="xs">
            {isentasComComite.map((o) => (
              <Badge key={o.id} variant="light" color="blue">
                {o.name} ({o.tenant_count} tenant{o.tenant_count === 1 ? '' : 's'})
              </Badge>
            ))}
          </Group>
        </Alert>
      )}

      {tenantsAmbiguos.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="orange"
               title={`${tenantsAmbiguos.length} tenant(s) alcançado(s) por mais de um comitê`}>
          <Text size="sm">
            Estes tenants pertencem a mais de uma organização. A escolha do
            comitê é determinística — sempre o mesmo — mas pode não ser o
            pretendido. Ajuste os vínculos de organização para resolver.
          </Text>
          <Group gap="xs" mt="xs">
            {tenantsAmbiguos.map((t) => (
              <Badge key={t.tenant_id} variant="light" color="orange">
                tenant #{t.tenant_id}: {t.committee_names.join(' / ')}
              </Badge>
            ))}
          </Group>
        </Alert>
      )}

      {/* Integrante posto no comitê sem pertencer à organização: a D3 decidiu
          que participar de comitê não concede tenant, e este é o caso residual
          dessa decisão — a pessoa aprovaria e tomaria 403 na hora de votar. */}
      {membrosSemAlcance.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red"
               title={`${membrosSemAlcance.length} integrante(s) sem alcance ao tenant`}>
          <Text size="sm">
            Aprova no papel e não alcança o tenant: está no comitê da
            organização, mas não tem o tenant atribuído. Vai <b>aprovar e
            tomar 403</b> na hora de votar.
          </Text>
          <Group gap="xs" mt="xs">
            {membrosSemAlcance.map((m) => (
              <Badge key={`${m.committee_id}-${m.user_id}`} variant="light" color="red">
                {m.full_name ?? `#${m.user_id}`} — {m.committee_name}{' '}
                ({m.tenants_fora.length} tenant{m.tenants_fora.length === 1 ? '' : 's'})
              </Badge>
            ))}
          </Group>
        </Alert>
      )}

      <DataCard flush
        title="Comitês de Mudança (CCM)"
        accent="primary"
        badge={{ value: comites.length, color: 'blue' }}
        loading={isLoading}
        extra={
          <Button size="xs" leftSection={<IconPlus size={14} />}
                  onClick={() => setNovoAberto(true)}>
            Novo Comitê
          </Button>
        }
      >
        {comites.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="xl" px="md">
            Nenhum comitê cadastrado. Enquanto não houver, o encaminhamento ao
            CCM aceita qualquer aprovador.
          </Text>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Quórum</Table.Th>
                <Table.Th>Membros</Table.Th>
                <Table.Th>Organizações</Table.Th>
                <Table.Th>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {comites.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{c.name}</Text>
                    {c.description && (
                      <Text size="xs" c="dimmed">{c.description}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>{c.cab_quorum_required}</Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={c.member_count < c.cab_quorum_required ? 'red' : 'blue'}
                    >
                      {c.member_count}
                    </Badge>
                    {c.member_count < c.cab_quorum_required && (
                      <Text size="xs" c="red">abaixo do quórum</Text>
                    )}
                  </Table.Td>
                  <Table.Td>{c.org_count}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Button size="compact-xs" variant="light"
                              leftSection={<IconUsers size={12} />}
                              onClick={() => setEditandoId(c.id)}>
                        Editar
                      </Button>
                      <ActionIcon size="sm" variant="subtle" color="red"
                                  aria-label={`Desativar ${c.name}`}
                                  onClick={() => desativar.mutate(c.id)}>
                        <IconPower size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </DataCard>

      <Modal opened={novoAberto} onClose={() => setNovoAberto(false)}
             title="Novo Comitê de Mudança" size="md">
        <Stack gap="sm">
          <TextInput label="Nome" required value={nome}
                     onChange={(e) => setNome(e.currentTarget.value)} />
          <Textarea label="Descrição" rows={2} value={descricao}
                    onChange={(e) => setDescricao(e.currentTarget.value)} />
          <NumberInput label="Quórum" min={1} value={quorum}
                       onChange={setQuorum}
                       description="Votos de aprovação necessários. Mínimo 1." />
          <Group justify="flex-end">
            <Button variant="default" size="xs"
                    onClick={() => setNovoAberto(false)}>Cancelar</Button>
            <Button size="xs" disabled={!nome.trim()} loading={criar.isPending}
                    onClick={() => criar.mutate()}>Criar</Button>
          </Group>
        </Stack>
      </Modal>

      {editandoId !== null && (
        <EditarComiteModal
          committeeId={editandoId}
          onClose={() => setEditandoId(null)}
          onSaved={invalidar}
        />
      )}
    </Stack>
  )
}

/** Edição de membros e organizações — ambos declarativos: manda a lista final. */
function EditarComiteModal({
  committeeId, onClose, onSaved,
}: { committeeId: number; onClose: () => void; onSaved: () => void }) {
  const [membros, setMembros] = useState<string[]>([])
  const [papeis, setPapeis] = useState<Record<string, ApproverRole>>({})
  const [orgs, setOrgs] = useState<string[]>([])
  const [carregado, setCarregado] = useState(false)

  const { data: comite } = useQuery({
    queryKey: ['ccm-committee', committeeId],
    queryFn: async () => (await ccmApi.get(committeeId)).data,
  })

  const { data: usuarios } = useQuery<{ items: Array<MixedUserRow & { full_name: string; email: string }> }>({
    queryKey: ['users-list-for-ccm'],
    queryFn: async () => (await api.get('/v1/users')).data,
    staleTime: 120_000,
  })

  // /lite é o payload enxuto (id+name) feito para seletor, já escopado às
  // orgs visíveis ao usuário. O /v1/organizations completo traz logo e mais.
  const { data: organizacoes } = useQuery<{ items: Array<{ id: number; name: string }> }>({
    queryKey: ['orgs-lite-for-ccm'],
    queryFn: async () => (await api.get('/v1/organizations/lite')).data,
    staleTime: 120_000,
  })

  if (comite && !carregado) {
    setMembros(comite.members.map((m) => String(m.user_id)))
    setPapeis(Object.fromEntries(
      comite.members.map((m) => [String(m.user_id), m.approver_role]),
    ))
    setOrgs(comite.organization_ids.map(String))
    setCarregado(true)
  }

  const salvar = useMutation({
    mutationFn: async () => {
      await ccmApi.setMembers(committeeId, membros.map((id) => ({
        user_id: Number(id),
        approver_role: papeis[id] ?? 'cab_member',
      })))
      await ccmApi.setOrganizations(committeeId, orgs.map(Number))
    },
    onSuccess: () => {
      onSaved()
      notifications.show({ color: 'green', message: 'Comitê atualizado.' })
      onClose()
    },
    onError: (e) => notifications.show({
      color: 'red', message: apiErrorMessage(e, 'Erro ao salvar o comitê.'),
    }),
  })

  const abaixoDoQuorum =
    comite != null && membros.length < comite.cab_quorum_required

  return (
    <Modal opened onClose={onClose} size="lg"
           title={comite ? `Editar ${comite.name}` : 'Editar comitê'}>
      <Stack gap="sm">
        <MultiSelect
          label="Membros do comitê"
          description="Só estes poderão votar nas GMUDs das organizações cobertas."
          placeholder="Buscar usuários"
          searchable
          // `/v1/users` mistura cadastros PENDENTES (`id: null`) — ver
          // utils/realUsers. Dois deles derrubam o MultiSelect.
          data={selectableUsers(usuarios?.items ?? []).map((u) => ({
            value: String(u.id), label: `${u.full_name} (${u.email})`,
          }))}
          value={membros}
          onChange={setMembros}
        />

        {membros.map((id) => {
          const u = usuarios?.items.find((x) => String(x.id) === id)
          return (
            <Group key={id} gap="sm" wrap="nowrap">
              <Text size="sm" style={{ flex: 1 }}>{u?.full_name ?? `#${id}`}</Text>
              <Select
                size="xs"
                w={200}
                aria-label={`Papel de ${u?.full_name ?? id}`}
                data={Object.entries(APPROVER_ROLE_LABELS).map(([v, l]) => ({
                  value: v, label: l,
                }))}
                value={papeis[id] ?? 'cab_member'}
                onChange={(v) => setPapeis((p) => ({
                  ...p, [id]: (v as ApproverRole) ?? 'cab_member',
                }))}
              />
            </Group>
          )
        })}

        {abaixoDoQuorum && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            O comitê tem {membros.length} membro(s) e o quórum é{' '}
            {comite?.cab_quorum_required}. Nenhuma GMUD dele será aprovável até
            que haja membros suficientes.
          </Alert>
        )}

        <MultiSelect
          label="Organizações cobertas"
          description="Cada organização pode ser coberta por um único comitê."
          placeholder="Buscar organizações"
          searchable
          leftSection={<IconBuilding size={14} />}
          data={(organizacoes?.items ?? []).map((o) => ({
            value: String(o.id), label: o.name,
          }))}
          value={orgs}
          onChange={setOrgs}
        />

        <Group justify="flex-end">
          <Button variant="default" size="xs" onClick={onClose}>Cancelar</Button>
          <Button size="xs" loading={salvar.isPending}
                  onClick={() => salvar.mutate()}>Salvar</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
